"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { cn } from "@/lib/utils";
import {
  Home,
  Search,
  DollarSign,
  Mail,
  ChevronLeft,
  ChevronRight,
  Wrench,
  HelpCircle,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLeftSidebar } from "@/contexts/left-sidebar-context";
import Logo from "@/components/logo/logo";

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  customAction?: () => void;
}

const navItems: NavItem[] = [
  { title: "Home", href: "/", icon: Home },
  { title: "Search", href: "/search", icon: Search },
  { title: "Pricing", href: "/pricing", icon: DollarSign },
  { title: "Contact", href: "/contact", icon: Mail },
  { title: "Tradesmen", href: "/services", icon: Wrench },
  {
    title: "Help",
    href: "/help",
    icon: HelpCircle,
    customAction: () => {
      const event = new CustomEvent("openHelpCommandPalette");
      window.dispatchEvent(event);
    },
  },
];

export function LeftSidebarNav({ id }: { id?: string }) {
  const { isExpanded, setIsExpanded } = useLeftSidebar();
  const [authModalOpen, setAuthModalOpen] = React.useState(false);
  const [authModalTab, setAuthModalTab] = React.useState<"signin" | "signup">(
    "signin",
  );
  const pathname = usePathname();
  const { data: session, status } = useSession();

  const isAdminPage = pathname?.startsWith("/admin");
  if (isAdminPage) return null;

  const openAuthModal = (tab: "signin" | "signup") => {
    setAuthModalTab(tab);
    setAuthModalOpen(true);
  };

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (pathname === "/" || pathname === "/home") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleItemClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    item: NavItem,
  ) => {
    if (item.customAction) {
      e.preventDefault();
      item.customAction();
    } else if (item.title === "Home") {
      handleHomeClick(e);
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      {isExpanded && (
        <div
          className="bg-background-overlay fixed inset-0 lg:block"
          style={{ zIndex: "var(--z-fixed)" }}
          onClick={() => setIsExpanded(false)}
          aria-hidden="true"
        />
      )}

      {/* Main Sidebar Wrapper - Now positioned at top starting from top-16 */}
      <div
        suppressHydrationWarning
        className={cn(
          "shadow-theme-xl fixed left-0 top-16 h-[calc(100vh-4rem)] border-r transition-all duration-300 ease-in-out",
          isExpanded ? "z-sidebar w-52" : "z-sidebar w-16",
          "bg-slate-500", // Lighter grey-blue background
          "after:bg-border-muted after:shadow-theme-sm after:absolute after:right-0 after:top-0 after:h-full after:w-[1px]",
          "hidden flex-col overflow-hidden md:flex",
        )}
        data-theme-bg="true"
        data-state={isExpanded ? "expanded" : "collapsed"}
      >
        {/* Floating Toggle Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "z-tooltip absolute -right-3 top-1/2 -translate-y-1/2",
            "h-8 w-8 rounded-full",
            "bg-gradient-to-br from-blue-500 to-blue-600", // Blue gradient for toggle button
            "border-border-hover border",
            "hover:shadow-theme-glow-lg shadow-theme-glow",
            "transition-all duration-300 ease-out",
            "hover:rotate-180 hover:scale-110",
            "flex items-center justify-center",
            "group",
          )}
          style={{ boxShadow: "0 0 20px rgba(217, 165, 20, 0.5)" }}
          aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
        >
          {isExpanded ? (
            <ChevronLeft className="text-button-text h-4 w-4 transition-transform group-hover:scale-110" />
          ) : (
            <ChevronRight className="text-button-text h-4 w-4 transition-transform group-hover:scale-110" />
          )}
        </button>

        {/* Navigation Items */}
        <nav
          id={id}
          aria-label="Main navigation"
          className="flex flex-col gap-3 p-2"
        >
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.href;

            const linkContent = (
              <Link
                key={item.href}
                href={item.href}
                onClick={(e) => handleItemClick(e, item)}
                data-tour={
                  item.title === "Search"
                    ? "search-button"
                    : item.title === "Contact"
                      ? "contact-button"
                      : item.title === "Tradesmen"
                        ? "tradesmen-button"
                        : item.title === "Help"
                          ? "help-button"
                          : undefined
                }
                className={cn(
                  "rounded-theme-md group flex items-center gap-3 px-3 py-2 text-sm transition-all duration-200",
                  isActive
                    ? "shadow-theme-inner border border-blue-400 bg-blue-600 font-medium text-white"
                    : "hover:shadow-theme-md text-gray-300 hover:bg-blue-500/30 hover:text-white",
                  !isExpanded && "justify-center",
                )}
                style={
                  isActive
                    ? { boxShadow: "inset 0 0 20px rgba(217, 165, 20, 0.2)" }
                    : undefined
                }
              >
                <Icon
                  className={cn(
                    "h-5 w-5 shrink-0 transition-all duration-200",
                    isActive
                      ? "text-white drop-shadow-[0_0_8px_rgba(59,130,246,0.5)]"
                      : "text-gray-300 group-hover:text-blue-400",
                  )}
                />
                {isExpanded && (
                  <span className="truncate transition-colors duration-200">
                    {item.title}
                  </span>
                )}
                {isExpanded && item.badge && (
                  <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                    {item.badge}
                  </span>
                )}
              </Link>
            );

            if (!isExpanded) {
              return (
                <Tooltip key={item.href}>
                  <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                  <TooltipContent side="right">
                    <p>{item.title}</p>
                  </TooltipContent>
                </Tooltip>
              );
            }

            return linkContent;
          })}
        </nav>

        {/* User Menu Panel */}
        <div className="absolute bottom-4 left-0 right-0 px-2">
          <div className={cn("pt-3", isExpanded ? "mx-1" : "")}>
            <div className={cn("flex", !isExpanded && "justify-center")}>
              {status === "authenticated" ? (
                <div className="rounded-full ring-2 ring-blue-400/50 ring-offset-2 ring-offset-slate-700 transition-all duration-200 hover:ring-blue-400"></div>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => openAuthModal("signin")}
                      className="relative border-blue-400/30 bg-blue-500/20 ring-2 ring-blue-400/30 transition-all duration-200 hover:bg-blue-500/40 hover:ring-blue-400"
                    >
                      <User className="h-4 w-4 text-blue-400" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>Log In / Register</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
